import { useCallback, useEffect, useRef, useState } from 'react';

import { runGlobalSearch } from '../api/search';
import { isAbortError } from '../lib/apiErrorHandling';
import type { GlobalSearchGroup } from '../types/search';
import { useApiBase } from './useApiBase';

type UseGlobalSearchOptions = {
  debounceMs?: number;
  limit?: number;
  initialQuery?: string;
};

export type UseGlobalSearchState = {
  query: string;
  setQuery: (next: string) => void;
  groups: GlobalSearchGroup[];
  isSearching: boolean;
  error: string | null;
  lastFetchedQuery: string;
  runImmediateSearch: (value?: string) => Promise<void>;
  reset: () => void;
};

const DEFAULT_DEBOUNCE_MS = 200;
const DEFAULT_LIMIT = 5;
export const GLOBAL_SEARCH_MIN_QUERY_LENGTH = 2;

export function useGlobalSearch(options: UseGlobalSearchOptions = {}): UseGlobalSearchState {
  const apiBase = useApiBase();
  const initialQueryRef = useRef(options.initialQuery ?? '');
  const [query, setQuery] = useState(initialQueryRef.current);
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedQuery, setLastFetchedQuery] = useState('');

  const controllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);

  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const limit = options.limit ?? DEFAULT_LIMIT;

  const abortOngoingRequest = useCallback(() => {
    requestIdRef.current += 1;
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, []);

  const clearDebounceTimer = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const runImmediateSearch = useCallback(
    async (value?: string) => {
      const nextQuery = typeof value === 'string' ? value : query;
      const trimmed = nextQuery.trim();

      if (trimmed.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
        abortOngoingRequest();
        clearDebounceTimer();
        setGroups([]);
        setLastFetchedQuery('');
        setError(null);
        setIsSearching(false);
        return;
      }

      abortOngoingRequest();
      clearDebounceTimer();
      const controller = new AbortController();
      controllerRef.current = controller;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setGroups([]);
      setLastFetchedQuery('');
      setIsSearching(true);
      setError(null);

      try {
        const payload = await runGlobalSearch(apiBase, trimmed, {
          limit,
          signal: controller.signal,
        });
        if (controller.signal.aborted || requestId !== requestIdRef.current) {
          return;
        }
        setGroups(payload.groups ?? []);
        setLastFetchedQuery(payload.query ?? trimmed);
      } catch (err) {
        if (controller.signal.aborted || isAbortError(err) || requestId !== requestIdRef.current) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to run search';
        setGroups([]);
        setLastFetchedQuery('');
        setError(message);
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
        }
        if (requestId === requestIdRef.current && !controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    },
    [abortOngoingRequest, apiBase, clearDebounceTimer, limit, query]
  );

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
      abortOngoingRequest();
      clearDebounceTimer();
      setGroups([]);
      setError(null);
      setIsSearching(false);
      setLastFetchedQuery('');
      return () => undefined;
    }

    abortOngoingRequest();
    setGroups([]);
    setLastFetchedQuery('');
    setError(null);
    setIsSearching(true);

    if (typeof window === 'undefined') {
      void runImmediateSearch(trimmed);
      return () => undefined;
    }

    clearDebounceTimer();
    debounceTimerRef.current = window.setTimeout(() => {
      void runImmediateSearch(trimmed);
    }, debounceMs);

    return () => {
      clearDebounceTimer();
    };
  }, [abortOngoingRequest, clearDebounceTimer, debounceMs, query, runImmediateSearch]);

  const reset = useCallback(() => {
    abortOngoingRequest();
    clearDebounceTimer();
    setGroups([]);
    setError(null);
    setIsSearching(false);
    setLastFetchedQuery('');
    setQuery(initialQueryRef.current);
  }, [abortOngoingRequest, clearDebounceTimer]);

  return {
    query,
    setQuery,
    groups,
    isSearching,
    error,
    lastFetchedQuery,
    runImmediateSearch,
    reset,
  };
}
